import { X } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { ChangeLogEvent } from "../data/cockpit-data";
import {
  displayTacticName,
  filterAndSortChangeLog,
  type ChangeLogSortKey,
  type TableSortDirection,
} from "../data/item-status-view";
import { badgeVariant, type Tone } from "../format";
import { AmazonStatePill, PAGE_SIZE, SortableHeader, TablePagination } from "./StatusTableParts";
import { TableControls } from "./TableControls";

function unique(values: string[]): string[] {
  return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
}

function option(value: string) {
  return { value, label: value };
}

function eventLabel(value: string): string {
  const label = value.replace(/_/g, " ").trim().toLowerCase();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : "Unknown";
}

/**
 * An event's tone. A retirement or reversal is the one that carries weight —
 * it is the machine UNDOING something, which is the event a reader scanning
 * this log is usually here to find.
 */
function eventTone(value: string): Tone {
  const lower = value.toLowerCase();
  if (/retir|revers|remove/.test(lower)) return "critical";
  if (/change|matur/.test(lower)) return "watch";
  return "ok";
}

function formatWhen(value: string | null): string {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toISOString().replace("T", " ").slice(0, 16)} UTC`;
}

type DecisionOverride = {
  action?: { from?: unknown; to?: unknown };
  target_group?: { name?: unknown; to?: unknown };
};

export function describeDecisionOverride(value: string | null): string | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const override = parsed as DecisionOverride;
    const from = typeof override.action?.from === "string" ? override.action.from : null;
    const to = typeof override.action?.to === "string" ? override.action.to : null;
    const targetName =
      typeof override.target_group?.name === "string"
        ? override.target_group.name
        : typeof override.target_group?.to === "string"
          ? override.target_group.to
          : null;
    if (from && to) {
      return `approved as ${to}, machine proposed ${from}${targetName ? ` → ${targetName}` : ""}`;
    }
    if (targetName) return `approved target group → ${targetName}`;
    return null;
  } catch {
    return null;
  }
}

/**
 * Every tactic application and operator decision, with publish outcomes only
 * where an application actually reached Amazon.
 *
 * Meteoric mirrored these filters into `window.location.search` with a raw
 * `history.replaceState`. That is NOT carried across: the portal is a
 * TanStack Router app and the shell owns the URL, so writing to it behind the
 * router's back desynchronises its state — a later navigation would restore a
 * location the router never recorded. The filters are local to the tab; the
 * `item` scope arrives as a prop from whoever opened this view.
 */
export function ChangeLogTable({ rows, item, onClearItem }: {
  rows: ChangeLogEvent[];
  /** Set when the reader came here from one keyword's row, to see only its history. */
  item?: string;
  onClearItem?: () => void;
}) {
  const [market, setMarket] = useState("");
  const [product, setProduct] = useState("");
  const [tactic, setTactic] = useState("");
  const [eventStatus, setEventStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortKey, setSortKey] = useState<ChangeLogSortKey>("applied_at");
  const [sortDir, setSortDir] = useState<TableSortDirection>("desc");
  const [page, setPage] = useState(0);

  const markets = useMemo(() => unique(rows.map((row) => row.market)), [rows]);
  const products = useMemo(() => unique(rows.flatMap((row) => (row.product ? [row.product] : []))), [rows]);
  const tactics = useMemo(() => unique(rows.map((row) => row.tactic_name)), [rows]);
  const events = useMemo(() => unique(rows.map((row) => row.event_status)), [rows]);

  const visible = useMemo(
    () =>
      filterAndSortChangeLog(
        rows,
        { market, product, tactic, eventStatus, dateFrom, dateTo, item: item ?? "" },
        sortKey,
        sortDir,
      ),
    [rows, market, product, tactic, eventStatus, dateFrom, dateTo, item, sortKey, sortDir],
  );
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paged = useMemo(
    () => visible.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [currentPage, visible],
  );

  function updateSelect(id: string, value: string) {
    if (id === "market") setMarket(value);
    if (id === "product") setProduct(value);
    if (id === "tactic") setTactic(value);
    if (id === "event") setEventStatus(value);
    setPage(0);
  }

  function updateSort(next: ChangeLogSortKey) {
    setSortDir(next === sortKey ? (sortDir === "desc" ? "asc" : "desc") : next === "applied_at" ? "desc" : "asc");
    setSortKey(next);
    setPage(0);
  }

  const sortLabel = { applied_at: "when", keyword: "keyword", market: "market", event_status: "event" }[sortKey];
  const directionLabel =
    sortKey === "applied_at"
      ? sortDir === "desc"
        ? "newest first"
        : "oldest first"
      : sortDir === "asc"
        ? "A to Z"
        : "Z to A";

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-ks-ink">Applications and decisions</h3>
        <TableControls
          selects={[
            { id: "market", label: "Market", value: market, options: markets.map(option) },
            { id: "product", label: "Product", value: product, options: products.map(option) },
            {
              id: "tactic",
              label: "Tactic",
              value: tactic,
              options: tactics.map((value) => ({ value, label: displayTacticName(value) })),
            },
            {
              id: "event",
              label: "Event type",
              value: eventStatus,
              options: events.map((value) => ({ value, label: eventLabel(value) })),
            },
          ]}
          textValue=""
          showText={false}
          onSelectChange={updateSelect}
          onTextChange={() => {}}
          resultCount={visible.length}
          totalCount={rows.length}
          countNoun="events"
        />
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">From</span>
            <Input
              type="date"
              className="min-h-9 w-40"
              value={dateFrom}
              onChange={(event) => {
                setDateFrom(event.target.value);
                setPage(0);
              }}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">To</span>
            <Input
              type="date"
              className="min-h-9 w-40"
              value={dateTo}
              onChange={(event) => {
                setDateTo(event.target.value);
                setPage(0);
              }}
            />
          </label>
          {item && onClearItem && (
            <div className="inline-flex min-w-0 items-center gap-2 rounded-lg bg-muted px-3 py-2 text-xs text-ks-ink">
              <span>
                History for <code className="font-mono font-semibold">{item}</code>
              </span>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Clear item history filter"
                data-nonmutating
                onClick={() => {
                  onClearItem();
                  setPage(0);
                }}
              >
                <X aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Sorted by {sortLabel}, {directionLabel}.
        </p>

        {visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No applications or decisions match these filters. Nothing is inferred and no mock data is shown.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader label="When" sortKey="applied_at" activeKey={sortKey} direction={sortDir} onSort={updateSort} />
                  <SortableHeader label="Keyword" sortKey="keyword" activeKey={sortKey} direction={sortDir} onSort={updateSort} />
                  <SortableHeader label="Market" sortKey="market" activeKey={sortKey} direction={sortDir} onSort={updateSort} />
                  <TableHead>Product</TableHead>
                  <TableHead>Tactic</TableHead>
                  <SortableHeader label="Event" sortKey="event_status" activeKey={sortKey} direction={sortDir} onSort={updateSort} />
                  <TableHead>Reason / change</TableHead>
                  <TableHead>Amazon result</TableHead>
                  <TableHead>Actor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((row, index) => {
                  const decision = row.tactic_id === "operator-decision";
                  const change = decision ? describeDecisionOverride(row.override_json) : null;
                  return (
                  <TableRow key={`${row.application_id}:${row.applied_at ?? "unknown"}:${row.event_status}:${index}`}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatWhen(row.applied_at)}
                    </TableCell>
                    <TableCell className="font-medium">
                      <span className="block max-w-52 truncate" title={row.keyword}>
                        {row.keyword}
                      </span>
                    </TableCell>
                    <TableCell>{row.market}</TableCell>
                    <TableCell title={row.asin ?? "no ASIN scope (market-wide)"}>
                      {row.product ? (
                        <span className="block max-w-44">
                          <span className="block truncate">{row.product}</span>
                          {row.variant && (
                            <span className="block truncate text-xs text-muted-foreground">{row.variant}</span>
                          )}
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-muted-foreground">{row.asin ?? "all"}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="block max-w-56 truncate font-medium" title={row.tactic_name}>
                        {displayTacticName(row.tactic_name)}
                      </span>
                      <span className="block max-w-56 truncate font-mono text-[10px] text-muted-foreground" title={row.tactic_id}>
                        {row.tactic_id}
                        {row.tactic_version === null ? "" : ` · v${row.tactic_version}`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={badgeVariant(eventTone(row.event_status))}>
                        {eventLabel(row.event_status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {decision && (row.note || change) ? (
                        <span className="block max-w-80 text-sm">
                          {row.note && <span className="block">{row.note}</span>}
                          {change && <span className="block text-muted-foreground">{change}</span>}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {decision ? (
                        <span className="text-sm italic text-muted-foreground">Not applicable</span>
                      ) : (
                        <AmazonStatePill state={row.publish_state} targetRef={row.target_ref} />
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="block font-medium">
                        {row.applied_by ?? (decision ? row.via : row.source) ?? "Unavailable"}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        source: {row.source ?? "unavailable"} · via: {row.via}
                      </span>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <TablePagination page={currentPage} pageCount={pageCount} visibleCount={visible.length} onPage={setPage} />
      </CardContent>
    </Card>
  );
}
